/**
 * Placeholder ("mock") board entries.
 *
 * A board must never look dead. When a board has zero real listings we render
 * a deterministic set of clearly-labelled mock entries so the ranking layout is
 * always visible. As soon as a single real listing is placed on that board the
 * mocks are not rendered at all — the callers only ask for mocks when the real
 * row list is empty.
 *
 * Mocks live only in the UI: nothing is written to the database, they are not
 * clickable, not indexed and never counted in Heat, prices or analytics.
 */

export type MockRow = {
  id: string;
  name: string;
  headline: string;
  type: string;
  heatCents: number;
  boosters: number;
};

const FIRST = [
  "Neon", "Solar", "Velvet", "Midnight", "Golden", "Crimson", "Atlas", "Nova",
  "Echo", "Lunar", "Ember", "Cobalt", "Vivid", "Aurora", "Pulse", "Onyx",
  "Sable", "Zenith", "Coral", "Halo",
];

const SECOND = [
  "Studio", "Collective", "Labs", "Records", "Agency", "Sound", "Media",
  "Works", "Club", "House", "Project", "Society", "Union", "Atelier",
];

const HEADLINES = [
  "Example entry — this slot is open",
  "Sample placement waiting for a real listing",
  "Demo position — claim it with Heat",
  "Placeholder while this board fills up",
  "Reserved preview slot",
];

const TYPES = ["brand", "creator", "startup", "artist", "app"];

/** Small deterministic hash so the same board always shows the same mocks. */
function hash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rng(seed: number) {
  let s = seed || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

/**
 * Deterministic mock rows for a board.
 * @param seedKey stable board key (category slug, or "general")
 */
export function mockBoardRows(seedKey: string, count = 5): MockRow[] {
  const rand = rng(hash(seedKey));
  const rows: MockRow[] = [];
  let heat = 9 + Math.floor(rand() * 12); // top mock sits at 9–20 Heat
  for (let i = 0; i < count; i++) {
    const name = `${FIRST[Math.floor(rand() * FIRST.length)]} ${
      SECOND[Math.floor(rand() * SECOND.length)]
    }`;
    rows.push({
      id: `mock-${seedKey}-${i}`,
      name,
      headline: HEADLINES[Math.floor(rand() * HEADLINES.length)]!,
      type: TYPES[Math.floor(rand() * TYPES.length)]!,
      heatCents: heat * 433,
      boosters: 1 + Math.floor(rand() * 3),
    });
    heat = Math.max(3, heat - (1 + Math.floor(rand() * 3)));
  }
  return rows;
}
